import {
  buildRatingTrend,
  isValidFreshdeskRating,
  buildRatingDistribution,
  calculateFeedbackMetrics,
  buildResponderPerformanceRows,
} from './utils';
import { useSnackbar } from 'notistack';
import { useMemo, useState } from 'react';
import { TICKET_FEEDBACK_STATUS } from 'enums';
import { MAX_FRESHDESK_RATING } from './constants';
import type { FeedbackFilterValues } from './types';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLanguageContext } from 'providers/language';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import type { FreshdeskFeedbackListFilters } from 'library/types';
import { Alert, Box, Button, Grid2 as Grid } from '@mui/material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import useFreshdeskFeedbackReport from './hooks/use-freshdesk-feedback-report';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import SentimentSatisfiedAltRoundedIcon from '@mui/icons-material/SentimentSatisfiedAltRounded';
import { FeedbackCharts, FeedbackTables, KpiCard, ReportFilters, ReportHeader } from './components';

const createEmptyFilters = (): FeedbackFilterValues => ({
  responderId: null,
  startDate: null,
  endDate: null,
  minRate: null,
  maxRate: null,
});

const toApiFilters = (filters: FeedbackFilterValues): FreshdeskFeedbackListFilters => ({
  responderId: filters.responderId,
  startDate: filters.startDate?.isValid() ? filters.startDate.startOf('day').toISOString() : null,
  endDate: filters.endDate?.isValid() ? filters.endDate.endOf('day').toISOString() : null,
  minRate: filters.minRate,
  maxRate: filters.maxRate,
});

const getDraftFilterKey = (filters: FeedbackFilterValues) => {
  const serializedFilters = toApiFilters(filters);

  return JSON.stringify({
    ...serializedFilters,
    startDate: filters.startDate && !filters.startDate.isValid() ? 'invalid' : serializedFilters.startDate,
    endDate: filters.endDate && !filters.endDate.isValid() ? 'invalid' : serializedFilters.endDate,
  });
};

const FreshdeskFeedbackReport = () => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const { language } = useLanguageContext();

  const [draftFilters, setDraftFilters] = useState<FeedbackFilterValues>(createEmptyFilters);
  const [appliedFilters, setAppliedFilters] = useState<FreshdeskFeedbackListFilters>(() =>
    toApiFilters(createEmptyFilters())
  );

  const {
    items,
    responders,
    loading,
    initialLoading,
    respondersLoading,
    hasError,
    respondersHasError,
    loadedAt,
    refresh,
    refreshResponders,
    refreshAll,
  } = useFreshdeskFeedbackReport(appliedFilters, true);

  const metrics = useMemo(() => calculateFeedbackMetrics(items), [items]);
  const distribution = useMemo(() => buildRatingDistribution(items), [items]);
  const trend = useMemo(() => buildRatingTrend(items, language), [items, language]);
  const responderRows = useMemo(
    () => buildResponderPerformanceRows(items, intl.formatMessage({ id: 'freshdeskFeedback.unknownResponder' })),
    [intl, items]
  );
  const ratedResponseCount = useMemo(
    () =>
      items.filter((item) => item.status === TICKET_FEEDBACK_STATUS.ANSWERED && isValidFreshdeskRating(item.rate))
        .length,
    [items]
  );
  const activeFilterCount = Object.values(appliedFilters).filter((value) => value !== null).length;
  const hasAppliedRatingFilter = appliedFilters.minRate !== null || appliedFilters.maxRate !== null;
  const hasPendingChanges = getDraftFilterKey(draftFilters) !== JSON.stringify(appliedFilters);

  const validateFilters = () => {
    const { startDate, endDate, minRate, maxRate } = draftFilters;

    if ((startDate && !startDate.isValid()) || (endDate && !endDate.isValid())) {
      enqueueSnackbar(intl.formatMessage({ id: 'freshdeskFeedback.validation.invalidDate' }), {
        variant: 'warning',
      });
      return false;
    }

    if (startDate && endDate && startDate.isAfter(endDate, 'day')) {
      enqueueSnackbar(intl.formatMessage({ id: 'freshdeskFeedback.validation.dateRange' }), {
        variant: 'warning',
      });
      return false;
    }

    if (minRate !== null && maxRate !== null && minRate > maxRate) {
      enqueueSnackbar(intl.formatMessage({ id: 'freshdeskFeedback.validation.ratingRange' }), {
        variant: 'warning',
      });
      return false;
    }

    return true;
  };

  const handleApplyFilters = () => {
    if (!validateFilters()) return;
    setAppliedFilters(toApiFilters(draftFilters));
  };

  const handleResetFilters = () => {
    const emptyFilters = createEmptyFilters();
    setDraftFilters(emptyFilters);
    setAppliedFilters(toApiFilters(emptyFilters));
  };

  const formatPercentage = (value: number) =>
    intl.formatNumber(value / 100, {
      style: 'percent',
      maximumFractionDigits: 1,
      minimumFractionDigits: 0,
    });
  const satisfactionTone =
    ratedResponseCount === 0
      ? 'primary'
      : metrics.satisfactionRate >= 80
        ? 'success'
        : metrics.satisfactionRate >= 60
          ? 'warning'
          : 'error';

  return (
    <Box sx={{ width: '100%', minWidth: 0, pb: 3 }}>
      <ReportHeader loading={loading || respondersLoading} loadedAt={loadedAt} onRefresh={refreshAll} />

      <ReportFilters
        value={draftFilters}
        responders={responders}
        activeFilterCount={activeFilterCount}
        hasPendingChanges={hasPendingChanges}
        loading={loading}
        respondersLoading={respondersLoading}
        respondersHasError={respondersHasError}
        onChange={setDraftFilters}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
        onRetryResponders={refreshResponders}
      />

      {hasAppliedRatingFilter && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <FormattedMessage id="freshdeskFeedback.filters.ratingScopeNotice" />
        </Alert>
      )}

      {hasError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={refresh}>
              <FormattedMessage id="freshdeskFeedback.retry" />
            </Button>
          }>
          <FormattedMessage id="freshdeskFeedback.loadError" />
        </Alert>
      )}

      {(!hasError || Boolean(loadedAt)) && (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <KpiCard
                icon={<MarkEmailReadOutlinedIcon />}
                title={<FormattedMessage id="freshdeskFeedback.metrics.totalRequests" />}
                value={intl.formatNumber(metrics.totalRequests)}
                helper={intl.formatMessage(
                  { id: 'freshdeskFeedback.metrics.totalRequestsHelper' },
                  { count: metrics.answeredCount }
                )}
                tone="primary"
                loading={initialLoading}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <KpiCard
                icon={<WarningAmberRoundedIcon />}
                title={<FormattedMessage id="freshdeskFeedback.metrics.lowRatings" />}
                value={intl.formatNumber(metrics.lowRatingCount)}
                helper={<FormattedMessage id="freshdeskFeedback.metrics.lowRatingsHelper" />}
                tone={metrics.lowRatingCount > 0 ? 'error' : 'success'}
                loading={initialLoading}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <KpiCard
                icon={<StarRoundedIcon />}
                title={<FormattedMessage id="freshdeskFeedback.metrics.averageRating" />}
                value={
                  ratedResponseCount
                    ? `${intl.formatNumber(metrics.averageRating, { maximumFractionDigits: 2 })} / ${MAX_FRESHDESK_RATING}`
                    : '—'
                }
                helper={intl.formatMessage(
                  { id: 'freshdeskFeedback.metrics.averageRatingHelper' },
                  { count: ratedResponseCount }
                )}
                tone="warning"
                loading={initialLoading}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <KpiCard
                icon={<SentimentSatisfiedAltRoundedIcon />}
                title={<FormattedMessage id="freshdeskFeedback.metrics.satisfactionRate" />}
                value={ratedResponseCount ? formatPercentage(metrics.satisfactionRate) : '—'}
                helper={<FormattedMessage id="freshdeskFeedback.metrics.satisfactionRateHelper" />}
                tone={satisfactionTone}
                loading={initialLoading}
              />
            </Grid>
          </Grid>

          <FeedbackCharts distribution={distribution} trend={trend} loading={initialLoading} />

          <FeedbackTables items={items} responderRows={responderRows} loading={loading} />
        </>
      )}
    </Box>
  );
};

export default FreshdeskFeedbackReport;
